import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  TrendingUp, TrendingDown, Sparkles, RefreshCw, Search, ArrowUp, ArrowDown, Minus,
  LayoutList, LineChart as LineChartIcon, Grid3x3, ExternalLink,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'

type Category = 'all' | 'companies' | 'technologies' | 'topics'
type ViewMode = 'rank' | 'timeline' | 'heatmap'

interface TrendEntity {
  name: string
  kind: 'company' | 'technology' | 'topic'
  mentions: number
  spark: number[]
  delta_pct: number
  sources: string[]
}
interface SuperData {
  days: string[]
  entities: TrendEntity[]
  movers_up: TrendEntity[]
  movers_down: TrendEntity[]
  kpis: {
    top_gainer: TrendEntity | null
    top_faller: TrendEntity | null
    total_mentions: number
    breakout: TrendEntity | null
    tracked: number
  }
  window_days: number
  total_articles_scanned: number
}

const TIMELINE_PALETTE = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e', '#ec4899', '#84cc16', '#a78bfa', '#fb923c']

const KIND_COLOR: Record<string, string> = {
  company:    'text-blue-light',
  technology: 'text-purple-400',
  topic:      'text-emerald-light',
}

const KIND_LABEL: Record<string, string> = {
  company:    'Company',
  technology: 'Tech',
  topic:      'Topic',
}

export default function SuperTrendsPage() {
  const [data, setData] = useState<SuperData | null>(null)
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState<Category>('all')
  const [days, setDays] = useState<number>(14)
  const [view, setView] = useState<ViewMode>('rank')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<'mentions' | 'delta' | 'name'>('mentions')
  const [selected, setSelected] = useState<string | null>(null)
  const [selectedArticles, setSelectedArticles] = useState<any[]>([])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/trends/super', { params: { category, days, limit: 80 } })
      setData(r.data)
    } finally {
      setLoading(false)
    }
  }, [category, days])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  // Fetch article drilldown when an entity is selected
  useEffect(() => {
    if (!selected) { setSelectedArticles([]); return }
    let mounted = true
    api.get(`/api/trends/entity/${encodeURIComponent(selected)}`)
      .then(r => { if (mounted) setSelectedArticles(r.data.articles || []) })
      .catch(() => { if (mounted) setSelectedArticles([]) })
    return () => { mounted = false }
  }, [selected])

  const rows = useMemo(() => {
    if (!data) return []
    let r = data.entities
    if (search) r = r.filter(e => e.name.toLowerCase().includes(search.toLowerCase()))
    const sorted = [...r]
    if (sortKey === 'mentions') sorted.sort((a, b) => b.mentions - a.mentions)
    else if (sortKey === 'delta') sorted.sort((a, b) => b.delta_pct - a.delta_pct)
    else sorted.sort((a, b) => a.name.localeCompare(b.name))
    return sorted
  }, [data, search, sortKey])

  // Timeline data: chart showing top 10 entities' sparklines merged
  const timelineData = useMemo(() => {
    if (!data || data.days.length === 0) return []
    const top10 = rows.slice(0, 10)
    return data.days.map((d, i) => {
      const point: Record<string, any> = { day: d.slice(5) }  // MM-DD
      for (const e of top10) point[e.name] = e.spark[i] ?? 0
      return point
    })
  }, [data, rows])

  // Heatmap: matrix of top 30 entities × days
  const heatmap = useMemo(() => {
    if (!data) return { top: [], max: 1 }
    const top = rows.slice(0, 30)
    const max = Math.max(1, ...top.flatMap(e => e.spark))
    return { top, max }
  }, [data, rows])

  return (
    <div className="space-y-4 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Sparkles size={24} className="text-blue" /> Super Trends
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Entities by mention volume + velocity across 460+ feeds · {data?.total_articles_scanned ?? 0} articles scanned
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* ── Filter bar ── */}
      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[11px]">
          {(['all', 'companies', 'technologies', 'topics'] as const).map(c => (
            <button key={c} type="button" onClick={() => setCategory(c)}
              className={clsx('px-2.5 py-1.5 capitalize',
                category === c ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover')}>
              {c}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[11px]">
          {[7, 14, 30, 60, 90].map(d => (
            <button key={d} type="button" onClick={() => setDays(d)}
              className={clsx('px-2.5 py-1.5',
                days === d ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover')}>
              {d}d
            </button>
          ))}
        </div>

        <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[11px]">
          {([
            { v: 'rank',     icon: LayoutList,     label: 'Rank'     },
            { v: 'timeline', icon: LineChartIcon,  label: 'Timeline' },
            { v: 'heatmap',  icon: Grid3x3,        label: 'Heatmap'  },
          ] as const).map(opt => {
            const Icon = opt.icon
            return (
              <button key={opt.v} type="button" onClick={() => setView(opt.v)}
                className={clsx('px-2.5 py-1.5 flex items-center gap-1',
                  view === opt.v ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover')}>
                <Icon size={11} /> {opt.label}
              </button>
            )
          })}
        </div>

        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search entity…"
            className="input pl-8 py-1.5 text-xs" />
        </div>

        <span className="text-[10px] text-text-muted ml-auto">
          {rows.length} of {data?.kpis.tracked ?? 0} entities
        </span>
      </div>

      {/* ── KPI tiles ── */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile label="Top gainer" icon={TrendingUp} tone="emerald"
            entity={data.kpis.top_gainer} suffix={data.kpis.top_gainer ? `+${data.kpis.top_gainer.delta_pct}%` : '—'} />
          <KpiTile label="Top faller" icon={TrendingDown} tone="rose"
            entity={data.kpis.top_faller} suffix={data.kpis.top_faller ? `${data.kpis.top_faller.delta_pct}%` : '—'} />
          <KpiTile label="New breakout" icon={Sparkles} tone="blue"
            entity={data.kpis.breakout} suffix={data.kpis.breakout ? `${data.kpis.breakout.mentions} mentions` : 'none'} />
          <div className="card p-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1">Total mentions</div>
            <div className="text-3xl font-bold text-text-primary mt-1">
              {(data.kpis.total_mentions ?? 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-text-muted mt-0.5">window: {data.window_days} days</div>
          </div>
        </div>
      )}

      {/* ── Main view ── */}
      {view === 'rank' && data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={clsx('card p-0 overflow-hidden', selected ? 'lg:col-span-2' : 'lg:col-span-3')}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-text-muted sticky top-0 bg-surface-card z-10">
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 w-8">#</th>
                    <th className="text-left py-2 px-3 cursor-pointer" onClick={() => setSortKey('name')}>Entity</th>
                    <th className="text-left py-2 px-3">Type</th>
                    <th className="text-right py-2 px-3 cursor-pointer" onClick={() => setSortKey('mentions')}>Mentions</th>
                    <th className="text-left py-2 px-3 w-24">{days}d trend</th>
                    <th className="text-right py-2 px-3 cursor-pointer" onClick={() => setSortKey('delta')}>Δ</th>
                    <th className="text-left py-2 px-3">Top sources</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e, i) => {
                    const isActive = selected === e.name
                    const dir = e.delta_pct > 5 ? 'up' : e.delta_pct < -5 ? 'down' : 'flat'
                    const sparkPoints = e.spark.map(v => ({ v }))
                    return (
                      <tr key={e.name}
                          onClick={() => setSelected(isActive ? null : e.name)}
                          className={clsx(
                            'border-b border-border/50 cursor-pointer transition-colors',
                            isActive ? 'bg-surface-hover' : 'hover:bg-surface-hover',
                          )}>
                        <td className="py-1.5 px-3 text-text-muted font-mono">{i + 1}</td>
                        <td className="py-1.5 px-3 font-medium text-text-primary">{e.name}</td>
                        <td className="py-1.5 px-3">
                          <span className={clsx('text-[10px] font-medium', KIND_COLOR[e.kind])}>{KIND_LABEL[e.kind]}</span>
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono text-text-primary">{e.mentions}</td>
                        <td className="py-1.5 px-3">
                          <Sparkline points={sparkPoints} width={90} height={18} tone={dir === 'up' ? 'emerald' : dir === 'down' ? 'rose' : 'muted'} showDot />
                        </td>
                        <td className="py-1.5 px-3 text-right">
                          <span className={clsx('inline-flex items-center gap-1 font-mono text-[11px]',
                            dir === 'up' ? 'text-emerald' : dir === 'down' ? 'text-rose' : 'text-text-muted')}>
                            {dir === 'up' ? <ArrowUp size={10} /> : dir === 'down' ? <ArrowDown size={10} /> : <Minus size={10} />}
                            {e.delta_pct > 0 ? '+' : ''}{e.delta_pct}%
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-[10px] text-text-muted truncate max-w-[200px]">
                          {(e.sources || []).slice(0, 3).join(', ')}
                        </td>
                      </tr>
                    )
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-text-muted">No entities match filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Drilldown panel */}
          {selected && (
            <div className="card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-text-primary">{selected}</h2>
                <button type="button" onClick={() => setSelected(null)} className="text-text-muted text-[10px] hover:text-text-primary">close</button>
              </div>
              {(() => {
                const ent = rows.find(e => e.name === selected)
                if (!ent) return null
                return (
                  <>
                    <div className="flex items-baseline gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-text-muted">Mentions</div>
                        <div className="text-2xl font-bold text-text-primary">{ent.mentions}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-text-muted">Δ {days}d</div>
                        <div className={clsx('text-2xl font-bold',
                          ent.delta_pct > 0 ? 'text-emerald' : ent.delta_pct < 0 ? 'text-rose' : 'text-text-muted')}>
                          {ent.delta_pct > 0 ? '+' : ''}{ent.delta_pct}%
                        </div>
                      </div>
                    </div>
                    <Sparkline points={ent.spark.map(v => ({ v }))} width={300} height={50} tone={ent.delta_pct > 0 ? 'emerald' : 'rose'} showDot />
                  </>
                )
              })()}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Recent articles</div>
                <div className="space-y-1.5 max-h-[360px] overflow-y-auto">
                  {selectedArticles.slice(0, 15).map((a, i) => (
                    <a key={i} href={a.url || '#'} target="_blank" rel="noopener"
                       className="block p-2 rounded border border-border/50 hover:bg-surface-hover text-xs">
                      <div className="flex items-start gap-1 text-text-primary leading-snug">
                        <span className="flex-1">{a.title}</span>
                        {a.url && <ExternalLink size={9} className="text-text-muted flex-shrink-0 mt-0.5" />}
                      </div>
                      <div className="text-[10px] text-text-muted mt-0.5">{a.source}</div>
                    </a>
                  ))}
                  {selectedArticles.length === 0 && <p className="text-[11px] text-text-muted italic">No recent articles.</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'timeline' && data && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-3">Top 10 entities · {days}-day mention timeline</h2>
          <ResponsiveContainer width="100%" height={440}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" />
              <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={{ background: '#18181c', border: '1px solid #26262c', fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {rows.slice(0, 10).map((e, i) => (
                <Line key={e.name} type="monotone" dataKey={e.name}
                      stroke={TIMELINE_PALETTE[i % TIMELINE_PALETTE.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === 'heatmap' && data && (
        <div className="card p-4 overflow-x-auto">
          <h2 className="text-sm font-semibold text-text-primary mb-3">Mention-intensity heatmap · top 30 entities × {days} days</h2>
          <table className="text-[10px]">
            <thead>
              <tr>
                <th></th>
                {data.days.map(d => (
                  <th key={d} className="text-text-muted font-normal px-0.5 py-1 text-[8px] rotate-45 origin-bottom-left">{d.slice(5)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmap.top.map(e => (
                <tr key={e.name} className="hover:bg-surface-hover cursor-pointer" onClick={() => { setView('rank'); setSelected(e.name) }}>
                  <td className="pr-2 py-1 text-text-secondary whitespace-nowrap">{e.name}</td>
                  {e.spark.map((v, i) => {
                    const intensity = v / heatmap.max
                    return (
                      <td key={i}
                          title={`${data.days[i]}: ${v}`}
                          style={{ background: `rgba(59,130,246,${intensity.toFixed(2)})` }}
                          className="w-5 h-5 border border-border/30" />
                    )
                  })}
                  <td className="pl-2 text-right text-text-muted font-mono">{e.mentions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top movers — always visible below the main view */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MoversCard title="Top gainers" icon={TrendingUp} tone="emerald" items={data.movers_up} onPick={setSelected} />
          <MoversCard title="Top fallers" icon={TrendingDown} tone="rose" items={data.movers_down} onPick={setSelected} />
        </div>
      )}
    </div>
  )
}

function KpiTile({
  label, icon: Icon, tone, entity, suffix,
}: { label: string; icon: React.ElementType; tone: 'emerald' | 'rose' | 'blue'; entity: TrendEntity | null; suffix: string }) {
  const toneClass = tone === 'emerald' ? 'text-emerald' : tone === 'rose' ? 'text-rose' : 'text-blue'
  return (
    <div className="card p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1">
        <Icon size={11} className={toneClass} /> {label}
      </div>
      <div className="text-lg font-bold text-text-primary mt-1 truncate">{entity?.name || '—'}</div>
      <div className={clsx('text-[11px] mt-0.5', toneClass)}>{suffix}</div>
    </div>
  )
}

function MoversCard({
  title, icon: Icon, tone, items, onPick,
}: { title: string; icon: React.ElementType; tone: 'emerald' | 'rose'; items: TrendEntity[]; onPick: (name: string) => void }) {
  const toneClass = tone === 'emerald' ? 'text-emerald' : 'text-rose'
  return (
    <div className="card p-4">
      <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
        <Icon size={14} className={toneClass} /> {title}
      </h2>
      <div className="space-y-1">
        {items.map((e, i) => (
          <button key={e.name} type="button" onClick={() => onPick(e.name)}
            className="w-full flex items-center justify-between text-xs hover:bg-surface-hover px-2 py-1 rounded">
            <span className="text-text-muted w-6">#{i + 1}</span>
            <span className="text-text-primary font-medium flex-1 text-left truncate">{e.name}</span>
            <span className="text-text-muted font-mono text-[10px] w-10 text-right">{e.mentions}</span>
            <Sparkline points={e.spark.map(v => ({ v }))} width={50} height={14} tone={tone} />
            <span className={clsx('font-mono text-[10px] w-14 text-right', toneClass)}>
              {e.delta_pct > 0 ? '+' : ''}{e.delta_pct}%
            </span>
          </button>
        ))}
        {items.length === 0 && <p className="text-[11px] text-text-muted italic">No movers in this window.</p>}
      </div>
    </div>
  )
}
